import { getAddress, getGeolocation } from '@/services/api/geolocation'
import { GET_RAW_ALERT } from '@/services/api/fleet'
import { GetAddressData } from '@/services/api/map'
import cloneDeep from 'lodash/cloneDeep'

export const Notification = {
  computed: {
    getAlertOptions() {
      return this.alerts()
    },
  },
  methods: {
    alerts() {
      const currentLang = this.$i18n.locale
      const data = cloneDeep(this.notifications || [])

      data.forEach((item) => {
        item.label = currentLang == 'en' ? item.descriptionEng : item.descriptionIdn
        item.category = (item.alertCategories || []).map((cat) =>
          currentLang == 'en' ? cat.categoryEng : cat.categoryIdn
        )
        item.key = item.alertCode
      })

      data.sort((a, b) => {
        const x = String(a.label || '').toLowerCase()
        const y = String(b.label || '').toLowerCase()

        return x.localeCompare(y)
      })

      return data.filter((item) => (item.scope == null ? true : item.scope))
    },

    getNotificationAlertCodes() {
      let alertCodes = [11, 1, 37, 2, 3, 10, 12, 7, 117, 118, 24, 23, 8, 25]
      if (this.hasAccess(['z1b-itemp:read'])) alertCodes = alertCodes.concat([17, 18])
      if (this.hasAccess(['z1d-fuelultrasonic:read', 'z1e-itank:read']))
        alertCodes = alertCodes.concat([13, 14])
      if (this.hasAccess(['z1l-pto:read'])) alertCodes = alertCodes.concat([62, 63])
      if (this.hasAccess(['z1f-idoor:read'])) alertCodes = alertCodes.concat([4, 5, 19, 20])
      if (this.hasAccess(['d7a-default-md-route:read'])) alertCodes.push(75)
      if (this.hasAccess(['notif_idle_parking:read'])) alertCodes = alertCodes.concat([15, 16])
      if (this.hasAccess(['notif_covered_remove:read'])) alertCodes.push(36)
      if (this.hasAccess(['notif_refrigerator:read'])) alertCodes = alertCodes.concat([21, 22])
      if (this.hasAccess(['e1f-web-excessivestopinsidegeofence:read'])) alertCodes.push(104)
      if (this.hasAccess(['teltonika-device:read'])) alertCodes = alertCodes.concat([94, 95])
      if (this.hasAccess(['seat_belt_alert:read'])) alertCodes.push(110)
      if (this.hasAccess(['fatigue_driving_alert:read'])) alertCodes.push(50)
      if (this.hasAccess(['infrared_blocked_alert:read'])) alertCodes.push(111)
      if (this.hasAccess(['front_object_detected_alert:read'])) alertCodes.push(113)
      if (this.hasAccess(['e1c-web-dms:read'])) alertCodes.push(49)
      if (this.hasAccess(['fleet-task-instant:read']))
        alertCodes = alertCodes.concat([143, 142, 141, 140, 139, 138, 137])
      if (this.hasAccess(['e1c-web-dms:read']))
        alertCodes = alertCodes.concat([51, 52, 53, 54, 55, 56, 112])
      if (this.hasAccess(['e1g-web-adas:read'])) alertCodes = alertCodes.concat([90, 91, 92])
      if (this.hasAccess(['e1k-adas-crash-indication:read'])) alertCodes.push(96)
      if (this.hasAccess(['e1l-adas-human-detection:read'])) alertCodes.push(134)
      if (this.hasAccess(['e1n-dms-driver-unrecognized:read']))
        alertCodes = alertCodes.concat([150, 149, 148, 152])
      if (this.hasAccess(['e1m-general-jammer-detection:read']))
        alertCodes = alertCodes.concat([88, 93])
      if (this.hasAccess(['e1p-general-storage-unavailable:read'])) alertCodes.push(123)
      if (this.hasAccess(['e1o-general-camera-offline:read'])) alertCodes.push(126)
      if (this.hasAccess(['tms-cold-chain:read'])) alertCodes.push(151)
      if (this.hasAccess(['e1q-brinks-cdm:read'])) alertCodes.push(122, 127, 128, 129)

      return alertCodes
    },

    getAlertList(opts) {
      let filteredData = (this.alertCode || []).map((item) => ({
        ...item,
        id: item.id ?? item.alertCode ?? item.key,
      }))

      const filters = [
        { permission: ['e1c-web-dms:read'], category: 'global.alert.type.dms' },
        { permission: ['e1g-web-adas:read'], category: 'global.alert.type.adas' },
      ]

      filters.forEach(({ permission, category }) => {
        if (!this.hasAccess(permission)) {
          const translatedCategory = this.$t(category)
          filteredData = filteredData.filter(
            (item) => !item.category?.some((value) => value === translatedCategory)
          )
        }
      })

      if (opts?.search) {
        const searchTerm = opts.search.toLowerCase()
        filteredData = filteredData.filter(
          (item) =>
            item.label.toLowerCase().includes(searchTerm) ||
            item.category?.some((category) => category.toLowerCase().includes(searchTerm))
        )
      }

      if (opts?.alertCategory?.length) {
        const translatedCategories = opts.alertCategory.map((category) => this.$t(category))
        filteredData = filteredData.filter((item) =>
          item.category?.some((category) => translatedCategories.includes(this.$t(category)))
        )
      }

      return {
        data: filteredData,
        ids: filteredData.map((item) => item.id),
        pagination: {
          page: 1,
          show: filteredData.length,
          total: filteredData.length,
        },
      }
    },

    async handleOpenPopup(alertId, { isCDMAlert = false, enrichAlert } = {}) {
      if (alertId == null) return null

      let rawAlert
      try {
        const response = await this.$apollo.query({
          query: GET_RAW_ALERT,
          fetchPolicy: 'no-cache',
          variables: {
            filter: {
              rawAlertId: alertId,
            },
          },
        })

        rawAlert = response?.data?.rawAlert
      } catch {
        this.$mcToast.error(this.$t('global.somethingWentWrong'))
        return null
      }

      const alert = rawAlert?.rawAlert
      if (!rawAlert?.isSuccessful || !alert) {
        this.$mcToast.error(rawAlert?.message || this.$t('global.somethingWentWrong'))
        return null
      }

      let address = '-'
      if (alert.latitude && alert.longitude) {
        try {
          if (this.isUsingCache()) {
            const geolocation = await getGeolocation({
              lat: alert.latitude,
              lon: alert.longitude,
            })
            address = getAddress(geolocation, alert.latitude, alert.longitude)?.name || '-'
          } else {
            const geolocation = await GetAddressData(alert.latitude, alert.longitude)
            address = geolocation.display_name || '-'
          }
        } catch {
          address = '-'
        }
      }

      const alertRuleIds = alert.alertRuleIds || []
      const subscribedRules = this.$store.state.notification.alertRuleIds || {}
      const matchesRule = (rules = []) => alertRuleIds.some((ruleId) => rules.includes(ruleId))
      const hasBackendClassification =
        typeof alert.isUrgent === 'boolean' || typeof alert.isImportant === 'boolean'
      const isUrgent = hasBackendClassification
        ? alert.isUrgent === true
        : !isCDMAlert && matchesRule(subscribedRules.urgentAlertRules)
      const isImportant = hasBackendClassification
        ? !isUrgent && alert.isImportant === true
        : !isUrgent &&
          !isCDMAlert &&
          (matchesRule(subscribedRules.importantAlertRules) ||
            matchesRule(subscribedRules.autoPopupAlertRules))

      const modifiedAlert = {
        ...alert,
        id: alert.rawAlertId ?? alertId,
        isUrgent,
        isImportant,
        isResolved: alert.isResolved || false,
        isRead: alert.isRead || false,
        address,
        mapUrl: `https://maps.googleapis.com/maps/api/staticmap?center=${alert.latitude},${alert.longitude}&zoom=15&size=374x200&maptype=roadmap&markers=color:red%7C${alert.latitude},${alert.longitude}&key=${process.env.VUE_APP_GOOGLE_MAP_API_KEY}`,
        ...(isCDMAlert ? { isCDMAlert: true } : {}),
      }

      const form = {
        reason: null,
        others: null,
      }
      const feedback = alert.lastUserFeedback?.feedback
      if (feedback) {
        const selectedReason = this.falseAlertReasons?.find?.((item) => item.value === feedback)
        form.reason = selectedReason?.key || 'others'
        form.others = selectedReason ? null : feedback
      }

      const finalAlert = enrichAlert ? await enrichAlert(modifiedAlert) : modifiedAlert

      await this.$store.dispatch('notification/addPopup', {
        alert: finalAlert,
        form,
        formOpen: false,
        lastOpenedAt: new Date().toISOString(),
      })

      return finalAlert
    },

    getAlertData(alert) {
      if (!alert) return null

      if (alert?.speed != null) {
        return {
          title: this.$t('global.object.speed'),
          value: `${Number.parseInt(alert.speed)} Km/${this.$t('global.time.hour')}`,
        }
      }

      const title = this.alertDataTitle(alert.alertCode)
      if (!title) return null

      return {
        title,
        value: this.handleShowValue(alert.alertCode, alert.formattedAlertData),
      }
    },

    alertDataTitle(alertCode) {
      switch (alertCode) {
        case 8:
          return this.$t('global.object.speed')
        case 13:
          return this.$t('notification.field.fuelAscent')
        case 14:
          return this.$t('notification.field.fuelDescent')
        case 15:
          return this.$t('report.fields.idleTime')
        case 16:
          return this.$t('report.fields.parkingTime')
        case 17:
          return this.$t('notification.field.temperature')
        case 18:
          return this.$t('notification.field.temperature')
        case 117:
          return this.$t('notification.field.drivingTime')
      }
    },

    alertTriggerTitle(alertCode) {
      switch (alertCode) {
        case 8:
          return this.$t('setting.list.notification.form.speedLimit')
        case 13:
          return this.$t('notification.field.fuelAscentLimit')
        case 14:
          return this.$t('notification.field.fuelDescentLimit')
        case 15:
          return this.$t('setting.list.trackerSensor.idleTimeLimit')
        case 16:
          return this.$t('setting.list.trackerSensor.parkingTimeLimit')
        case 17:
          return this.$t('notification.field.temperatureLimit')
        case 18:
          return this.$t('notification.field.temperatureLimit')
        case 117:
          return this.$t('notification.field.drivingTimeLimit')
      }
    },

    handleShowValue(alertCode, value, type = 'data') {
      const alertCodes = [8, 13, 14, 15, 16, 17, 18, 117]
      if (!alertCodes.includes(alertCode)) return undefined
      if (!value || value == '-' || value == '0.00') return '-'

      switch (alertCode) {
        case 8:
          return `${Number.parseInt(value)} Km/${this.$t('global.time.hour')}`
        case 13:
          return `${value} Liter`
        case 14:
          return `${value} Liter`
        case 15:
          return this.convertMinutetoDay(value)
        case 16:
          return this.convertMinutetoDay(value)
        case 17:
          return `${value} °C`
        case 18:
          return `${value} °C`
        case 117:
          if (type === 'trigger' && !this.hasAccess('e4k-alert-driving-time-threshold:read')) {
            return `4 ${this.$t('global.text.time.hour')}`
          }
          return this.convertMinutetoDay(value)
      }
    },
  },
}

export default Notification
